import Head from 'next/head'
import Link from 'next/link'
import Header from '../src/local/Header'
import ErrorBoundary from '../src/local/ErrorBoundary'
import { HolmiAppDarkFooterBrand } from '../src/local/HolmiAppLogo'
import styles from '../styles/RedesignBemutato.module.css'

export default function Bemutato() {
  const currentYear = new Date().getFullYear()
  const yearsOnMarket = Math.max(1, currentYear - 2020)

  return (
    <ErrorBoundary>
      <div className={styles.site}>
        <Head>
          <title>HolmiApp – Bemutató</title>
        </Head>
        <Header />

        {/* PAGE HEADER */}
        <div className={styles.pageHeader}>
          <div className={styles.eyebrow}><span className={styles.eyebrowDot} />Az applikációról</div>
          <h1 className={styles.pageTitle}>Találd meg, ami <em>téged érdekel</em></h1>
          <p className={styles.pageSub}>Az önkormányzatokkal együttműködve összegyűjtöttük a neked fontos információkat. Hírek, események, közlemények – mind egy helyen, gyorsan és egyszerűen.</p>
        </div>

        {/* FEATURE BLOCKS */}
        <div className={styles.features}>

          {/* 01 — Tartalom */}
          <div className={styles.featureBlock}>
            <div className={styles.featureText}>
              <div className={styles.featureNumber}>01 — Tartalom</div>
              <h2 className={styles.featureTitle}>Minden fontos helyi info egy helyen</h2>
              <p className={styles.featureDesc}>Az önkormányzatokkal együttműködve összegyűjtöttük a téged érdeklő információkat. Hírek, események, közérdekű adatok – nem kell több oldalt böngészni.</p>
              <div className={styles.featureHighlight}>
                <svg viewBox="0 0 24 24" fill="none" stroke="#1A7A55" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z" /></svg>
                <p>Ha valamit többször használsz, jelöld meg kedvencként – így mindig egy kattintásra lesz tőled.</p>
              </div>
            </div>
            <div className={styles.featureVisual}>
              <svg className={styles.illus} viewBox="0 0 300 260" xmlns="http://www.w3.org/2000/svg">
                <rect x="30" y="10" width="240" height="240" rx="20" fill="#1A2E4A" />
                <rect x="46" y="28" width="208" height="38" rx="8" fill="rgba(255,255,255,0.08)" />
                <circle cx="67" cy="47" r="10" fill="#27AE78" />
                <rect x="84" y="41" width="80" height="7" rx="3" fill="rgba(255,255,255,0.6)" />
                <rect x="84" y="52" width="50" height="5" rx="2" fill="rgba(255,255,255,0.25)" />
                <rect x="185" y="38" width="52" height="18" rx="9" fill="rgba(39,174,120,0.25)" />
                <rect x="46" y="76" width="208" height="52" rx="10" fill="rgba(255,255,255,0.06)" />
                <rect x="60" y="87" width="120" height="8" rx="3" fill="rgba(255,255,255,0.5)" />
                <rect x="60" y="100" width="90" height="6" rx="2" fill="rgba(255,255,255,0.2)" />
                <path d="M220 93 l5 5 l10-10" stroke="#27AE78" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                <rect x="46" y="138" width="208" height="52" rx="10" fill="rgba(255,255,255,0.06)" />
                <rect x="60" y="149" width="100" height="8" rx="3" fill="rgba(255,255,255,0.5)" />
                <rect x="60" y="162" width="70" height="6" rx="2" fill="rgba(255,255,255,0.2)" />
                <rect x="46" y="200" width="208" height="36" rx="10" fill="#27AE78" />
                <rect x="80" y="212" width="140" height="8" rx="3" fill="rgba(255,255,255,0.8)" />
              </svg>
            </div>
          </div>

          {/* 02 — Elérhetőség (reverse, dark visual) */}
          <div className={`${styles.featureBlock} ${styles.reverse}`}>
            <div className={styles.featureText}>
              <div className={styles.featureNumber}>02 — Elérhetőség</div>
              <h2 className={styles.featureTitle}>Nemcsak a saját városod – a környék is</h2>
              <p className={styles.featureDesc}>Az applikációban nem csak a saját, hanem a környező települések eseményeit is láthatod. Ha egy szomszéd városban nincs még HolmiApp, jelezd nekünk – mi továbbítjuk az önkormányzat felé.</p>
              <div className={styles.featureHighlight}>
                <svg viewBox="0 0 24 24" fill="none" stroke="#1A7A55" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="2" y1="12" x2="22" y2="12" /><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" /></svg>
                <p>Feltétel: az adott település is szerepeljen a HolmiApp-ban. Ha nincs, jelezd – mi továbbítjuk az önkormányzat felé.</p>
              </div>
            </div>
            <div className={styles.featureVisualDark}>
              <svg className={styles.illus} viewBox="0 0 300 260" xmlns="http://www.w3.org/2000/svg">
                <circle cx="150" cy="130" r="100" fill="rgba(39,174,120,0.08)" stroke="#27AE78" strokeWidth="1" strokeDasharray="6 4" />
                <circle cx="150" cy="130" r="65" fill="rgba(39,174,120,0.10)" stroke="#27AE78" strokeWidth="1" strokeDasharray="4 3" />
                <circle cx="150" cy="130" r="32" fill="rgba(39,174,120,0.18)" stroke="#27AE78" strokeWidth="1.5" />
                <circle cx="150" cy="130" r="10" fill="#27AE78" />
                <circle cx="150" cy="130" r="5" fill="white" />
                <circle cx="205" cy="85" r="8" fill="#1A2E4A" stroke="#27AE78" strokeWidth="1.5" />
                <circle cx="205" cy="85" r="4" fill="#27AE78" />
                <circle cx="95" cy="100" r="8" fill="#1A2E4A" stroke="#27AE78" strokeWidth="1.5" />
                <circle cx="95" cy="100" r="4" fill="#27AE78" />
                <circle cx="210" cy="165" r="8" fill="#1A2E4A" stroke="#27AE78" strokeWidth="1.5" />
                <circle cx="210" cy="165" r="4" fill="#27AE78" />
                <circle cx="98" cy="170" r="8" fill="#1A2E4A" stroke="#27AE78" strokeWidth="1.5" />
                <circle cx="98" cy="170" r="4" fill="#27AE78" />
                <line x1="150" y1="130" x2="205" y2="85" stroke="#27AE78" strokeWidth="1" strokeDasharray="3 3" opacity="0.5" />
                <line x1="150" y1="130" x2="95" y2="100" stroke="#27AE78" strokeWidth="1" strokeDasharray="3 3" opacity="0.5" />
                <line x1="150" y1="130" x2="210" y2="165" stroke="#27AE78" strokeWidth="1" strokeDasharray="3 3" opacity="0.5" />
                <line x1="150" y1="130" x2="98" y2="170" stroke="#27AE78" strokeWidth="1" strokeDasharray="3 3" opacity="0.5" />
                <rect x="118" y="148" width="64" height="20" rx="10" fill="#1A2E4A" />
                <rect x="125" y="155" width="50" height="6" rx="3" fill="rgba(255,255,255,0.6)" />
              </svg>
            </div>
          </div>

          {/* 03 — Hatékonyság */}
          <div className={styles.featureBlock}>
            <div className={styles.featureText}>
              <div className={styles.featureNumber}>03 — Hatékonyság</div>
              <h2 className={styles.featureTitle}>Kimélve a mobilod – és az egyenleged</h2>
              <p className={styles.featureDesc}>Gondos tervezéssel egy nagyon hatékony adatforgalmi rendszert dolgoztunk ki. Amit egyszer letöltöttél, nem töltjük le újra. A te adatkeretedre vigyázunk.</p>
              <div className={styles.featureHighlight}>
                <svg viewBox="0 0 24 24" fill="none" stroke="#1A7A55" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12" /></svg>
                <p>Minimális adatforgalom – az applikáció okosan gazdálkodik a sávszélességgel, mobilneten is gond nélkül működik.</p>
              </div>
            </div>
            <div className={styles.featureVisual}>
              <svg className={styles.illus} viewBox="0 0 300 260" xmlns="http://www.w3.org/2000/svg">
                <rect x="20" y="30" width="260" height="180" rx="14" fill="#1A2E4A" />
                <rect x="34" y="46" width="232" height="12" rx="4" fill="rgba(255,255,255,0.06)" />
                <rect x="34" y="64" width="232" height="120" rx="8" fill="rgba(255,255,255,0.04)" />
                <polyline points="50,160 80,130 110,145 140,100 170,115 200,80 230,90 250,70" fill="none" stroke="#27AE78" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                <circle cx="200" cy="80" r="5" fill="#27AE78" />
                <circle cx="250" cy="70" r="5" fill="#27AE78" />
                <rect x="34" y="194" width="70" height="8" rx="4" fill="rgba(39,174,120,0.4)" />
                <rect x="34" y="206" width="50" height="6" rx="3" fill="rgba(255,255,255,0.15)" />
                <rect x="190" y="194" width="76" height="18" rx="9" fill="rgba(39,174,120,0.2)" />
                <rect x="200" y="200" width="56" height="6" rx="3" fill="rgba(39,174,120,0.7)" />
              </svg>
            </div>
          </div>

          {/* 04 — Sebesség (reverse, dark visual) */}
          <div className={`${styles.featureBlock} ${styles.reverse}`}>
            <div className={styles.featureText}>
              <div className={styles.featureNumber}>04 — Sebesség</div>
              <h2 className={styles.featureTitle}>Gyors – mert az időd drága</h2>
              <p className={styles.featureDesc}>Minden adat a telefonodon van. Az applikáció offline is működik – ha egyszer letöltötted, nem kell várnod. Amire már nincs szükséged, egyszerűen törölheted.</p>
              <div className={styles.featureHighlight}>
                <svg viewBox="0 0 24 24" fill="none" stroke="#1A7A55" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
                <p>Offline működés – az adatok a készüléken vannak, nem kell minden nyitásnál újra letölteni.</p>
              </div>
            </div>
            <div className={styles.featureVisualDark}>
              <svg className={styles.illus} viewBox="0 0 300 260" xmlns="http://www.w3.org/2000/svg">
                <circle cx="150" cy="120" r="90" fill="rgba(39,174,120,0.06)" stroke="#27AE78" strokeWidth="1" strokeDasharray="5 4" />
                <circle cx="150" cy="120" r="65" fill="rgba(26,46,74,0.5)" stroke="#1A2E4A" strokeWidth="2" />
                <circle cx="150" cy="120" r="55" fill="#1A2E4A" />
                <line x1="150" y1="120" x2="150" y2="86" stroke="white" strokeWidth="2.5" strokeLinecap="round" />
                <line x1="150" y1="120" x2="172" y2="108" stroke="#27AE78" strokeWidth="3" strokeLinecap="round" />
                <circle cx="150" cy="120" r="5" fill="#27AE78" />
                <text x="150" y="175" textAnchor="middle" fontSize="13" fontWeight="700" fill="#27AE78" fontFamily="Plus Jakarta Sans, sans-serif">GYORS</text>
                <text x="150" y="192" textAnchor="middle" fontSize="10" fill="rgba(255,255,255,0.4)" fontFamily="Inter, sans-serif">Offline mód aktív</text>
                <rect x="56" y="212" width="188" height="28" rx="14" fill="#27AE78" />
                <text x="150" y="231" textAnchor="middle" fontSize="11" fontWeight="700" fill="white" fontFamily="Plus Jakarta Sans, sans-serif">Azonnali betöltés</text>
              </svg>
            </div>
          </div>

          {/* 05 — Design */}
          <div className={styles.featureBlock}>
            <div className={styles.featureText}>
              <div className={styles.featureNumber}>05 — Design</div>
              <h2 className={styles.featureTitle}>Egyszerű, átlátható, letisztult</h2>
              <p className={styles.featureDesc}>Felesleges grafikai megoldások helyett a letisztult, gyors, hatékony használatra helyeztük a hangsúlyt. Minden funkció pontosan ott van, ahol keresed.</p>
              <div className={styles.featureHighlight}>
                <svg viewBox="0 0 24 24" fill="none" stroke="#1A7A55" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
                <p>Minden funkció egy-két kattintással elérhető – bonyolult menük és felesleges elemek nélkül.</p>
              </div>
            </div>
            <div className={styles.featureVisual}>
              <svg className={styles.illus} viewBox="0 0 300 260" xmlns="http://www.w3.org/2000/svg">
                <rect x="60" y="10" width="180" height="240" rx="24" fill="#1A2E4A" />
                <rect x="74" y="28" width="152" height="206" rx="16" fill="#F4F7FB" />
                <rect x="86" y="40" width="128" height="18" rx="6" fill="#1A2E4A" />
                <rect x="96" y="44" width="60" height="8" rx="4" fill="rgba(255,255,255,0.5)" />
                <rect x="86" y="66" width="128" height="42" rx="10" fill="white" stroke="#DDE3EC" strokeWidth="1" />
                <rect x="96" y="75" width="70" height="6" rx="3" fill="#1A2E4A" opacity="0.7" />
                <rect x="96" y="86" width="50" height="5" rx="2" fill="#6B7A8D" opacity="0.5" />
                <rect x="168" y="72" width="34" height="14" rx="7" fill="#E8F8F2" />
                <rect x="172" y="77" width="26" height="4" rx="2" fill="#27AE78" />
                <rect x="86" y="116" width="128" height="42" rx="10" fill="white" stroke="#DDE3EC" strokeWidth="1" />
                <rect x="96" y="125" width="80" height="6" rx="3" fill="#1A2E4A" opacity="0.7" />
                <rect x="96" y="136" width="55" height="5" rx="2" fill="#6B7A8D" opacity="0.5" />
                <rect x="86" y="166" width="128" height="42" rx="10" fill="white" stroke="#DDE3EC" strokeWidth="1" />
                <rect x="96" y="175" width="65" height="6" rx="3" fill="#1A2E4A" opacity="0.7" />
                <rect x="96" y="186" width="45" height="5" rx="2" fill="#6B7A8D" opacity="0.5" />
                <rect x="168" y="172" width="34" height="14" rx="7" fill="#27AE78" />
                <rect x="172" y="177" width="26" height="4" rx="2" fill="rgba(255,255,255,0.8)" />
              </svg>
            </div>
          </div>
        </div>

        {/* STATS BAR */}
        <div className={styles.statsBar}>
          <div className={styles.statItem}><div className={styles.statNum}>5<span>+</span></div><div className={styles.statLabel}>Aktív település</div></div>
          <div className={styles.statItem}><div className={styles.statNum}><span>∞</span></div><div className={styles.statLabel}>Folyamatos bővítés</div></div>
          <div className={styles.statItem}><div className={styles.statNum}>{yearsOnMarket}<span>+</span></div><div className={styles.statLabel}>Éve a piacon</div></div>
          <div className={styles.statItem}><div className={styles.statNum}>5<span>×</span></div><div className={styles.statLabel}>Modul egy csomagban</div></div>
        </div>

        {/* CTA */}
        <section className={styles.ctaSection}>
          <h2 className={styles.ctaTitle}>Töltsd le és próbáld ki</h2>
          <p className={styles.ctaSub}>Ingyenes, Android és iOS rendszerre. Telepítés után válaszd ki a településed – és máris minden ott van.</p>
          <div className={styles.ctaBadges}>
            <a className={styles.storeBadge} href="https://play.google.com/store/apps/details?id=hu.holmiapp" target="_blank" rel="noreferrer">
              <svg viewBox="0 0 24 24"><path d="M3.18 23.76a2 2 0 001.6-.19l12.4-7.2-2.93-2.93-11.07 10.32zM.42 1.92A2 2 0 000 3.08v17.84a2 2 0 00.42 1.16L.54 22.2 12.78 10 .54 1.8.42 1.92zM22.06 10.36L19.3 8.74l-3.23 3.24 3.23 3.24 2.79-1.62a2 2 0 000-3.24zM4.78.43L17.18 7.63l-2.93 2.93L4.18.63A2 2 0 014.78.43z" /></svg>
              Google Play
            </a>
            <a className={styles.storeBadge} href="https://apps.apple.com/hu/app/id1570622313" target="_blank" rel="noreferrer">
              <svg viewBox="0 0 24 24"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" /></svg>
              App Store
            </a>
          </div>
        </section>

        {/* FOOTER */}
        <footer className={styles.footer}>
          <HolmiAppDarkFooterBrand brandClassName={styles.footerBrand} textClassName={styles.footerLogo} />
          <div className={styles.footerLinks}>
            <Link href="/">Főoldal</Link>
            <Link href="/Bemutato">Bemutató</Link>
            <Link href="/Telepulesek">Települések</Link>
            <Link href="/Onkormanyzat">Önkormányzat</Link>
			<Link href="/Galeria">Galéria</Link>
          </div>
          <span className={styles.footerCopy}>© 2026 HolmiApp. Minden jog fenntartva.</span>
        </footer>
      </div>
    </ErrorBoundary>
  )
}
